import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useRelationTypeActivation, useRelationTypeDetail } from "~/features/admin/relationTypes/hooks";
import { toRelationTypeDetailUI } from "~/features/admin/relationTypes/utils/transformers";
import { RelationTypeDetailPresenter } from "./RelationTypeDetailPresenter";
import { RelationTypeFormDialog } from "./RelationTypeFormDialog";

interface RelationTypeDetailProps {
  relationTypeId: string;
  /** Back action: close the sheet (peek) or navigate to the list (full page). */
  onBack: () => void;
}

/**
 * Reusable from both the full-page route and the Peek side panel. Owns data,
 * the edit dialog, and the activation confirm flow; renders the presenter.
 */
export function RelationTypeDetail({ relationTypeId, onBack }: RelationTypeDetailProps) {
  const { t } = useTranslation();
  const { data } = useRelationTypeDetail(relationTypeId);
  const { onActivate, onDeactivate, processing } = useRelationTypeActivation();
  const [pendingAction, setPendingAction] = useState<"activate" | "deactivate" | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const raw = data.relationTypes?.[0];
  const detail = useMemo(() => (raw ? toRelationTypeDetailUI(raw) : null), [raw]);

  if (!detail) {
    return (
      <div className="rounded-[var(--radius-md)] border border-border-default bg-white px-5 py-12 text-center text-14-regular text-fg-2">
        {t("ui.notFound")}
      </div>
    );
  }

  const handleConfirm = async () => {
    if (pendingAction === "activate") {
      await onActivate({ id: detail.id });
    } else if (pendingAction === "deactivate") {
      await onDeactivate({ id: detail.id });
    }
    setPendingAction(null);
  };

  return (
    <>
      <RelationTypeDetailPresenter
        relationType={detail}
        onBack={onBack}
        onEdit={() => setEditOpen(true)}
        onActivate={() => setPendingAction("activate")}
        onDeactivate={() => setPendingAction("deactivate")}
        pendingAction={pendingAction}
        processing={processing}
        onConfirm={handleConfirm}
        onCancelConfirm={() => setPendingAction(null)}
      />
      <RelationTypeFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        relationType={{
          id: detail.id,
          name: detail.name,
          sourceCategoryId: detail.sourceCategoryId,
          targetCategoryId: detail.targetCategoryId,
        }}
      />
    </>
  );
}
